import React from 'react';

/**
 * GoldenAIWelcome - Welcome page for Golden AI (Learning AI)
 *
 * Light color palette to distinguish from active learning environment
 * Explains capacity, position in loop, and vision without timeline promises
 */
function GoldenAIWelcome({ onBackToSIFISO, onEnterGoldenAI }) {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#F5F5F0',
      color: '#2A2A2A'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <header style={{ marginBottom: '50px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px'
          }}>
            <div>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: '800',
                color: '#D4AF37',
                marginBottom: '10px',
                letterSpacing: '-0.5px'
              }}>
                GOLDEN AI
              </h1>
              <p style={{
                fontSize: '1.2rem',
                fontStyle: 'italic',
                color: '#6B6B6B'
              }}>
                Learning AI
              </p>
            </div>
            <button
              onClick={onBackToSIFISO}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2A2A2A',
                color: '#F5F5F0',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3A3A3A'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2A2A2A'}
            >
              ← Return to SIFISO
            </button>
          </div>

          <div style={{
            height: '3px',
            background: 'linear-gradient(to right, #D4AF37, transparent)',
            marginBottom: '20px'
          }} />

          <p style={{
            fontSize: '1.4rem',
            fontWeight: '600',
            color: '#2A2A2A',
            marginBottom: '15px'
          }}>
            Augmented Thinking
          </p>
          <p style={{
            fontSize: '1rem',
            color: '#6B6B6B',
            lineHeight: '1.7',
            maxWidth: '700px'
          }}>
            The capacity to think with AI responsibly, not through it. Understanding AI limits,
            using it as a thinking partner, maintaining cognitive sovereignty.
          </p>
        </header>

        {/* Main Content */}
        <main>
          {/* Status Box */}
          <div style={{
            backgroundColor: '#FFFBF0',
            border: '2px solid #D4AF37',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '40px'
          }}>
            <div style={{
              fontSize: '0.85rem',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: '#D4AF37',
              marginBottom: '15px'
            }}>
              Now Active — 12 Levels of AI Literacy
            </div>
            <p style={{
              fontSize: '1.05rem',
              color: '#2A2A2A',
              lineHeight: '1.7',
              marginBottom: '15px'
            }}>
              Golden AI extends systems literacy into the AI era. Learn to work with artificial
              intelligence without losing your own capacity to think. Understand AI limitations,
              spot hallucinations and bias, and maintain cognitive sovereignty.
            </p>
            <p style={{
              fontSize: '0.95rem',
              color: '#6B6B6B',
              lineHeight: '1.7',
              marginBottom: '25px'
            }}>
              This is not a tutorial on prompting. It is not training you to depend on AI.
              It is building your ability to remain human while working with machines.
            </p>
            <button
              onClick={onEnterGoldenAI}
              style={{
                padding: '14px 32px',
                backgroundColor: '#D4AF37',
                color: '#2A2A2A',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#C4A030';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#D4AF37';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Enter Golden AI →
            </button>
          </div>

          {/* What This Builds */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              What This Capacity Builds
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Understanding what AI can and cannot do',
                  'Recognizing when to trust AI and when to question it',
                  'Using AI to clarify your thinking, not replace it',
                  'Identifying bias, hallucination, and structural limits',
                  'Maintaining cognitive sovereignty in the AI era'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: idx < 4 ? '15px' : '0',
                    fontSize: '1rem',
                    lineHeight: '1.6',
                    color: '#2A2A2A'
                  }}>
                    <span style={{
                      color: '#D4AF37',
                      marginRight: '12px',
                      fontSize: '1.3rem',
                      fontWeight: '700'
                    }}>
                      •
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Position in Loop */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              Position in the Loop
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <p style={{
                fontSize: '1rem',
                color: '#2A2A2A',
                lineHeight: '1.7',
                marginBottom: '20px'
              }}>
                Golden AI extends systems literacy into the AI era. It requires the foundation
                built in Golden Learn—the ability to reason about how systems work.
              </p>
              <div style={{
                backgroundColor: '#F5F5F0',
                padding: '20px',
                borderRadius: '8px',
                borderLeft: '4px solid #D4AF37'
              }}>
                <p style={{
                  fontSize: '0.95rem',
                  color: '#2A2A2A',
                  lineHeight: '1.7',
                  fontStyle: 'italic',
                  margin: 0
                }}>
                  "You enter Golden AI after completing at least Level 10 of Golden Learn.
                  By then, you understand variables, conditionals, loops, and basic abstraction—
                  the minimum needed to reason about AI, not just use it."
                </p>
              </div>
            </div>
          </section>

          {/* What's Included */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              What's Included
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <p style={{
                fontSize: '1rem',
                color: '#2A2A2A',
                lineHeight: '1.7',
                marginBottom: '20px'
              }}>
                Golden AI includes 12 comprehensive levels:
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Understanding how large language models work (without mathematics)',
                  'Critical evaluation frameworks (when to trust, when to verify)',
                  'Pattern recognition for AI limitations and failure modes',
                  'Responsible use principles (AI as partner, not crutch)',
                  'Application exercises (using AI without losing your own thinking)'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: idx < 4 ? '12px' : '0',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    color: '#6B6B6B'
                  }}>
                    <span style={{
                      color: '#D4AF37',
                      marginRight: '12px',
                      fontSize: '1.1rem'
                    }}>
                      →
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Principle */}
          <section>
            <div style={{
              backgroundColor: '#2A2A2A',
              color: '#F5F5F0',
              borderRadius: '10px',
              padding: '30px',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '1.2rem',
                fontWeight: '600',
                lineHeight: '1.7',
                margin: 0
              }}>
                AI as thinking partner, not answer machine.
                <br />
                <span style={{ color: '#D4AF37', fontStyle: 'italic' }}>
                  Augment capacity, don't erode it.
                </span>
              </p>
            </div>
          </section>
        </main>

        {/* Footer Note */}
        <footer style={{
          marginTop: '60px',
          paddingTop: '30px',
          borderTop: '1px solid #E5E5D5',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '0.9rem',
            color: '#6B6B6B',
            lineHeight: '1.6'
          }}>
            Entry Requirement: Complete Level 10 of Golden Learn before starting Golden AI.
            <br />
            AI literacy requires foundational systems thinking.
          </p>
        </footer>
      </div>
    </div>
  );
}

export default GoldenAIWelcome;
